import { RouteComponentProps } from "react-router";
import "../../../../styles/css/table.scss";
import "../../../../App.css";
import { getAirStatus } from "../../../../Common/Helper";
import Spinner from "../../../../Components/Navigation/LoadingSpinner/Spinner";
import FormPanelInfo from "../../../../Components/Headings/FormHeading/FormPanelInfo";
import InputTextBox from "../../../../Components/Inputs/TextBox/InputTextBox";
import { appContext } from "../../../../AppContext";
import { DateTime } from "luxon";
import { Icons } from "../../../../Common/AllsvgIcons";
import { EnvironmentalZone } from "./DataModels";
import { DateHelper } from "../../../../Common/DateHelper";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";

class ViewMostRecentEnvironmentalDataByZone extends IbssPage<IProps, IState>{
  private labels = appContext().labels;
  private session = appContext().sessionStorageProvider;
  constructor(props: IProps)
  {
    super(props);
    this.state =
    {
      isLoading: false,
      zone: {
        zoneId: '',
        zoneName:  '',
        zoneLastUpdated: DateHelper.null(),
        TVOC: 0,
        PM25: 0,
        PM10: 0,
        CO2: 0,
        CO: 0,
        soundLevel: 0,
        humidity: 0,
        temperature: 0,
        IAQStatus: 0,
      },
      formFieldsData: []
    };
  }

  public componentDidMount(): void
  {
    const { history, match }: IProps = this.props;
    const { zoneId, buildingid } = match.params;

    this.pageTitle = this.labels.HubLabelSustainabilityAnalyticsZones;
    if (this.session.getBuildingId() !== parseInt(buildingid)) 
    {
      const temp = window.location.pathname.slice(0, window.location.pathname.lastIndexOf('/'));
      history.push(temp + "/" + this.session.getBuildingId())
    }
    this.getZoneData(parseInt(zoneId));
  }

  private async getZoneData(zoneId: number): Promise<void>
  {
    this.setState({ isLoading: true });
    try
    {
      const response = await appContext().ibssApiClientV1.v1.byNodeid.environmentalZoneData.byEnvzoneid.get<EnvironmentalZone>({
        nodeId: this.session.getBuildingId(),
        envzoneid: zoneId,
      });

      const zone = EnvironmentalZoneView.fromAPIModel(response);
      await this.setStateAsync({ zone: zone });

      this.makeFormFields();
    }
    finally
    {
      this.setState({ isLoading: false });
    }
  }
  
  private makeFormFields(): void
  {
    const formFields: IFormFields[] = [
      {
        name: 'Env_Zone_Name',
        label: this.labels.HubLabelName,
        value: this.state.zone.zoneName,
        readonly: true
      },
      {
        name: 'IAQ_Status',
        label: this.labels.HubLabelStatus,
        value: getAirStatus(this.state.zone.IAQStatus),
        readonly: true
      },
      {
        name: 'Env_Temperature',
        label: this.labels.HubLabelTemperature,
        value: `${this.state.zone.temperature}\u00b0C`,
        readonly: true
      },
      {
        name: 'Env_Humidity',
        label: this.labels.HubLabelHumidity,
        value: `${this.state.zone.humidity}%`,
        readonly: true
      },
      {
        name: 'Env_Sound_Level',
        label: this.labels.HubLabelNoise,
        value: `${this.state.zone.soundLevel} dB`,
        readonly: true
      },
      {
        name: 'Env_CO',
        label: this.labels.HubLabelCo,
        value: `${this.state.zone.CO} ppm`,
        readonly: true
      },
      {
        name: 'Env_CO2',
        label: this.labels.HubLabelCO2,
        value: `${this.state.zone.CO2} ppm`,
        readonly: true
      },
      {
        name: 'Env_PM10',
        label: this.labels.HubLabelPM10,
        value: `${this.state.zone.PM10} ppm`,
        readonly: true
      },
      {
        name: 'Env_PM25',
        label: this.labels.HubLabelPM25,
        value: `${this.state.zone.PM25} ppm`,
        readonly: true
      },
      {
        name: 'Env_TVOC',
        label: this.labels.HubLabelTVOC,
        value: `${this.state.zone.TVOC} ppm`,
        readonly: true
      },
      {
        name: 'Env_Zone_LastUpdated',
        label: this.labels.HubLabelLastUpdate,
        value: `${this.state.zone.zoneLastUpdated.toLocaleDateTimeString()}`,
        readonly: true
      },
    ];
    this.setState({ formFieldsData: formFields })
  }

  openHistoricalData = () => {
    const { history }: IProps = this.props;
    const startDate = DateTime.local().minus({ days: 7 }).toFormat("yyyy-MM-dd");
    const endDate = DateTime.local().toFormat("yyyy-MM-dd");
    history.push(`/sustainability-analytics-zones/${this.session.getBuildingId()}/${this.state.zone.zoneId}/historical?StartDate=${startDate}&EndDate=${endDate} `)
  }


  render() {
    return (
      <>
        <div className="rightPanel-main-content-form">
          <div className="form-panel">
            {(this.state.isLoading || this.state.isLoading) && <Spinner />}
            <FormPanelInfo
              formHeading={this.state.zone.zoneName}
              iconSrc={Icons.ZoneIcon}
            />
            <form className="form-panel-form">
              {this.state.formFieldsData.map((obj: IFormFields, ind: number) => {
                return (
                  <div className="row" key={ind}>
                    <InputTextBox {...obj} />
                  </div>
                )
              })}
            </form>
          </div>
        </div>
        <div className="rightPanel-main-button-group">
          <div className="d-flex justify-content-end w-100">
            <button className="button-primary" onClick={() => this.openHistoricalData()}>{this.labels.HubButtonHistoricalData}</button>
          </div>
        </div>
      </>
    );
  }
}

export interface IProps extends RouteComponentProps<IQueryParams>
{
}

interface IQueryParams 
{
    buildingid: string;
    zoneId: string;
}

interface IFormFields
{
  name: string,
  label: string,
  value: string,
  readonly: boolean
}
export interface IState
{
  isLoading: boolean;
  zone: EnvironmentalZoneView;
  formFieldsData: IFormFields[];
}

class EnvironmentalZoneView
{
    public zoneId = '';
    public zoneName = '';
    public zoneLastUpdated =  DateHelper.null();
    public TVOC = 0;
    public PM25 = 0;
    public PM10 = 0;
    public CO2 = 0;
    public CO = 0;
    public soundLevel = 0;
    public humidity = 0;
    public temperature = 0;
    public IAQStatus = 0;

    public static fromAPIModel(data: EnvironmentalZone): EnvironmentalZoneView
    {
      return {
          zoneId: data.Env_Zone_Id,
          zoneName: data.Env_Zone_Name,
          zoneLastUpdated: DateHelper.fromIsoByNode(data.Env_Zone_LastUpdated, data.Node_Id),
          TVOC: data.Env_TVOC,
          PM25: data.Env_PM25,
          PM10: data.Env_PM10,
          CO2: data.Env_CO2,
          CO: data.Env_CO,
          soundLevel: data.Env_Sound_Level,
          humidity: data.Env_Humidity,
          temperature: data.Env_Temperature,
          IAQStatus: data.IAQ_Status,
      }
    }
}

export default ViewMostRecentEnvironmentalDataByZone;
